/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ReactElement } from 'react';
import { styled } from 'styled-components';
import { Paragraph } from '@motili/ui';

interface FileCardProps {
    fileName: string;
    tradeLicense?: boolean;
    handleExpirationInputChange?: (key: string) => (expiration: string) => void;
    handleTrashClick: () => void;
    startDate?: string;
}

const shortenFileName = (text: string) => {
    if (text.length > 25) {
        return '...' + text.substr(text.length - 25, text.length);
    }
    return text;
};

export default function FileCard({
    fileName,
    tradeLicense,
    handleTrashClick,
    handleExpirationInputChange,
    startDate,
}: FileCardProps): ReactElement {
    return (
        <StyledFileContainer>
            <StyledFileContent>
                <Paragraph>{shortenFileName(fileName)}</Paragraph>
                <StyledTrashIcon
                    onClick={handleTrashClick}
                    className={`fa fa-trash`}
                />
            </StyledFileContent>
            {tradeLicense && handleExpirationInputChange ? (
                <StyledRow>
                    <StyledDateContainer>
                        <Paragraph>Start Date</Paragraph>
                        <StyledDateInput
                            type='date'
                            onChange={event =>
                                handleExpirationInputChange('validFrom')(
                                    event.target.value
                                )
                            }
                        />
                    </StyledDateContainer>
                    <StyledDateContainer>
                        <Paragraph>End Date</Paragraph>
                        <StyledDateInput
                            disabled={!startDate}
                            min={startDate}
                            type='date'
                            onChange={event =>
                                handleExpirationInputChange('validTo')(
                                    event.target.value
                                )
                            }
                        />
                    </StyledDateContainer>
                </StyledRow>
            ) : null}
        </StyledFileContainer>
    );
}

const StyledDateInput = styled.input`
    flex: 1;
`;
const StyledRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledDateContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledTrashIcon = styled.i`
    font-size: ${({ theme }) => theme.fontSizes['22']};
    color: ${({ theme }) => theme.colors.blue400};
`;

const StyledFileContainer = styled.div`
    width: 90%;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.green};
    padding: 20px;
    margin: 20px auto;
`;

const StyledFileContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    & > p {
        margin: 0;
        font-weight: bold;
        font-size: ${({ theme }) => theme.fontSizes['16']};
    }
`;
